import SaporeApiClient from "./saporeApiClient";
import BearerAuthorizationManager from "./BearerAuthorizationManager";
import SaporeRemoteRequester from "./SaporeRemoteRequester";
import Session from "./Session";
import FirebaseAuthenticationMethod from "./FirebaseAuthenticationMethod";
import FakeAuthenticationMethod from "./FakeAuthenticationMethod";
import {action, computed, makeObservable, observable} from "mobx";
import Business from "./business";

export class ApplicationFactory {
    async create() {
        const authenticationMethod = this._authenticationMethod();
        const session = new Session(authenticationMethod)
        let application = new Application(authenticationMethod, session);
        await application.setUpApiClient();
        authenticationMethod.withApplication(application)
        await authenticationMethod.subscribeToAuthEvents();
        session.withApplication(application);
        return application;
    }

    _authenticationMethod() {
        const fakeAuthenticationIsEnabled = process.env.REACT_APP_AUTHENTICATION_METHOD === 'FAKE';
        return fakeAuthenticationIsEnabled ? new FakeAuthenticationMethod() : new FirebaseAuthenticationMethod();
    }
}

export default class Application {
    constructor(authenticationMethod, session) {
        this._business = new Business()
        this._authenticationMethod = authenticationMethod
        this._session = session
        this._saporeApiClient = null;
        this._order = session.getOrder();
        this._customer = session.getCustomer();
        this._backend_url = null;
        this._orderPriceList = null;

        makeObservable(this, {
            _order: observable,
            order: computed,
            _customer: observable,
            customer: computed,
            setOrder: action,
        })
    }

    async setUpApiClient() {
        const requester = await this._setUpRequester();
        this._saporeApiClient = new SaporeApiClient(requester, this._authenticationMethod, this);
    }

    business() {
        return this._business
    }

    get order() {
        return this._order
    }

    set order(value) {
        this._order = value
        this.session().addOrder(value)
    }

    get customer() {
        return this._customer
    }

    set customer(value) {
        this._customer = value
    }

    wasOrderChosen() {
        return this.order !== null
    }

    getCustomer() {
        return this.session().getCustomer()
    }

    setCustomer(customer) {
        this._customer = customer
        this.session().addCustomer(customer)
    }

    hasCustomer() {
        return this.session().hasCustomer()
    }

    setOrder(order) {
        this._order = order;
        this.session().addOrder(order)
    }

    getOrder() {
        return this._session.getOrder()
    }

    hasOrder() {
        return this.session().hasOrder();
    }

    removeOrder() {
        this._order = null
        this.session().removeOrder()
    }

    setOrderPriceList(priceList) {
        this._orderPriceList = priceList;
    }

    getOrderPriceList() {
        return this._orderPriceList;
    }

    async _getBackendUrl() {
        const backendUrl = process.env.REACT_APP_BACKEND_URL
        if (backendUrl) {
            return backendUrl
        }
        const response = await fetch('/api')
        if (!response.ok) {
            throw new Error('Failed to fetch API URL');
        }
        const json = await response.json();
        return json["API_URL"];
    }

    async _setUpRequester() {
        const bearerAuthorizationManager = new BearerAuthorizationManager(this.session());
        this._backend_url = await this._getBackendUrl();
        return new SaporeRemoteRequester(this._backend_url, bearerAuthorizationManager);
    }

    session() {
        return this._session
    }

    remoteApiUrl() {
        return this._backend_url
    }

    apiClient() {
        return this._saporeApiClient
    }

    recoverPassword(email) {
        return this._authenticationMethod.recoverPassword(email)
    }

}